"use client";

import { ComponentProps, memo, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { TabButton } from "./TabButton";

export type TabProps = {
  key: string;
  name: string;
  label: string;
  description?: string;
  isDisabled?: boolean;
  floatBadge?: string;
};

export type HorizontalTabProps = ComponentProps<"nav"> & {
  tabs: TabProps[];
  activeKey?: string;
  onTabClick?: (key: string) => void;
};

export const HorizontalTab = memo(
  ({ tabs, activeKey, onTabClick, ...props }: HorizontalTabProps) => {
    const [active, setActive] = useState(activeKey ?? tabs[0].key);

    useEffect(() => {
      setActive(activeKey ?? tabs[0].key);
    }, [activeKey]);

    return (
      <nav
        {...props}
        className={twMerge(
          "-mb-px flex gap-x-7 border-b border-gray-200",
          props.className
        )}
        aria-label="horizontal-tabs"
      >
        {tabs.map((tab) => (
          <TabButton
            key={tab.key}
            id={tab.key}
            aria-label={tab.label}
            isActive={tab.key === active}
            aria-selected={tab.key === active}
            onClick={() => {
              if (!tab.isDisabled) {
                setActive(tab.key);
                onTabClick?.(tab.key);
              }
            }}
            disabled={tab.isDisabled}
          >
            <div className="flex items-center gap-x-2">
              {tab.name}
              {tab.floatBadge && (
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-yellow-500 text-xs font-semibold text-white">
                  <p className="-translate-y-[1px]">{tab.floatBadge}</p>
                </div>
              )}
            </div>
          </TabButton>
        ))}
      </nav>
    );
  }
);

HorizontalTab.displayName = "HorizontalTab";
